import { createContext, useContext, useEffect, useState } from 'react';
import { tenantApi, type TenantConfig, type TenantFeatures } from '../services/api';

const DEFAULT_TENANT_CONFIG: TenantConfig = {
  tenant_id: 'dev',
  branding: { name: 'Flight Connection Designer', logo_url: null, theme_color: null },
  ui: { environment_badge: null },
  authentication: {
    mode: 'email',
    password_recovery: true,
    user_management: true,
  },
  features: {
    custom_market_metrics: false,
    schedule_explorer: true,
    schedule_visualize: true,
    connection_analysis: true,
    schedule_comparison: true,
    partnership_analysis: true,
    bank_optimizer: true,
    mct_management: false,
    mct_dataset_mode: false,
    workspaces: false,
    org_sharing: false,
    route_optimizer: false,
    hub_reports: false,
    schedule_merge: false,
    feedback: false,
    connection_value_profile: false,
    agent_api: false,
    agent_investigations: false,
  },
  defaults: {
    hub_airport: '',
    carrier: '',
    min_connection_to: 1,
    max_connection_to: 4,
    min_connection_from: 1,
    max_connection_from: 4,
    connection_type: 'All',
  },
  data: {
    visible_sample_week_ids: null,
    global_schedule_status_url: null,
  },
};

export const TenantContext = createContext<TenantConfig>(DEFAULT_TENANT_CONFIG);

export const useTenantConfig = () => useContext(TenantContext);

export const useFeatureEnabled = (feature: keyof TenantFeatures): boolean => {
  const config = useTenantConfig();
  return config.features[feature] === true;
};

export const useTenantConfigLoader = () => {
  const [config, setConfig] = useState<TenantConfig>(DEFAULT_TENANT_CONFIG);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    tenantApi.getConfig()
      .then(setConfig)
      .catch((err) => {
        console.error('Failed to load tenant config, using defaults:', err);
      })
      .finally(() => setIsLoading(false));
  }, []);

  return { config, isLoading };
};
